/**
 * Scroll memory for the One-Window Drilldown (UX-RW-03): leaving the overview
 * for one of its branches remembers where the list was, and coming back puts the
 * reader at the same place. A position is remembered ONLY when the destination
 * is a branch of the TPIN, so choosing a TPIN afresh always starts at the top,
 * and a position is used once (restoring consumes it).
 */

import { failedListPath, parseInvoicePath, parseTpinPath, pendingListPath } from '@domain/tpinPaths'

const positions = new Map<string, number>()

/** The key for one overview: its TPIN and period (the same scope the data belongs to). */
export function scrollKey(scope: { readonly tin: string; readonly from: string; readonly to: string }): string {
  return `${scope.tin}|${scope.from}|${scope.to}`
}

/** Called as the overview unmounts, with the URL it is leaving for. */
export function rememberScrollOnLeave(key: string, destinationPath: string, y: number): void {
  const destination = parseTpinPath(destinationPath)
  if (destination === null || destination.dbName === null) {
    positions.delete(key)
    return
  }
  positions.set(key, y)
}

/** The key for one worklist: the failed (S3) or pending (S4) list of one branch. */
export function listScrollKey(kind: 'failed' | 'pending', dbName: string): string {
  return `list|${kind}|${dbName}`
}

/** Called as a worklist unmounts, with the URL it is leaving for: only an invoice detail is a way back to it. */
export function rememberListScrollOnLeave(key: string, destinationPath: string, y: number): void {
  if (parseInvoicePath(destinationPath) === null) {
    positions.delete(key)
    return
  }
  positions.set(key, y)
}

/**
 * Called as an invoice detail unmounts, with the URL it is leaving for: a worklist's remembered
 * position survives only when the reader is going back to that very list. While the URL is still an
 * invoice (a re-render or a development double-mount) nothing is forgotten.
 */
export function leaveInvoice(dbName: string, destinationPath: string): void {
  if (parseInvoicePath(destinationPath) !== null) return
  if (destinationPath !== failedListPath(dbName)) positions.delete(listScrollKey('failed', dbName))
  if (destinationPath !== pendingListPath(dbName)) positions.delete(listScrollKey('pending', dbName))
}

/** Put the reader back where they left, once; a position of zero needs no scroll. */
export function restoreScroll(key: string): void {
  const y = positions.get(key) ?? 0
  positions.delete(key)
  if (y > 0) window.scrollTo(0, y)
}
